/** A Vivrelle User */
export type User = {
  /** string UUID of the user in EPCC */
  id: string;
  /** Object containing all of a user's social media handles */
  socialMedia: {
    instagram?: string;
    facebook?: string;
    tiktok?: string;
  };

  /** The user's Birthday */
  birthday?: string;

  /** Status */
  status: USER_STATUS | null;

  /** Determines whether a user is a Vivrelle Admin or not */
  isAdmin: boolean;

  /** Determines whether a user is a Vivrelle Brand Ambassador or not */
  isBrandAmbassador: boolean;

  /** The user's name */
  name: string;
  firstName?: string;
  lastName?: string;

  /** The user's email address */
  email: string;

  /** User's main phone number */
  phone: string;

  /** DateTime which the user's auth token expires at, in seconds */
  tokenExpiry?: number;

  subscriptions: {
    email?: boolean;
    wishlist?: boolean;
    sms?: boolean;
  };

  referralCode: string;

  /** EP `referrer_code`: the partner / promo code the member signed up with (e.g. THOMPSON) */
  referrerCode?: string;

  /** ID representing user's payment provider customer (currently only Stripe) */
  paymentCustomerId: string;

  /** EP subscription order ID, representing user's current subscription */
  subscriptionOrderId: string;

  /** Whether customer is locked; if true, this overrides member's status */
  isLocked: boolean;

  /** Whether customer is past due; if true */
  isPastDue: boolean;

  /** Whether customer has fraud flag; if true, restricts self-service reactivation */
  isFraud: boolean;

  /** Whether customer has the `New Member` account flag (first-order gating) */
  isNewMember: boolean;

  /** The user's Privee status */
  priveeStatus?: PRIVEE_STATUS;

  /** ISO8601 date when the user was accepted into Réservé */
  priveeAcceptanceDate?: string;

  /** ISO8601 date when the user's Réservé membership was frozen */
  priveeFrozenDate?: string;

  /** Whether the customer has indicated interest in Privee */
  isInterestedInPrivee: boolean;
};

export enum PRIVEE_STATUS {
  ACTIVE = 'active',
  INVITED = 'invited',
  FROZEN = 'frozen',
  CANCELLED = 'cancelled',
  NONE = 'none',
  INITIATION_FAILED = 'initiation_failed',
  PENDING = 'pending'
}

export enum ACCOUNT_FLAG_VALUE {
  FRAUD = 'Fraud',
  ORDER_ACTION_REQUIRED = 'Order Action Required',
  FIRST_SWAP = '1st Swap',
  ACCOUNT_LOCKED = 'Account Locked',
  NEW_MEMBER = 'New Member',
  PAST_DUE = 'Past Due',
  NEW_DOWNGRADE = 'New Downgrade',
  NEW_UPGRADE = 'New Upgrade',
  NONE = 'None',
  REACTIVATED = 'Reactivated'
}

/**
 * The raw data returned from EPCC when a user logs in.
 * This is copy/pasted from the response that the /api/login endpoint
 * returns from logging in via the BFF microservice.
 */
export type RawEPCCUser = {
  id: string;
  type: 'customer';
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  birthday: string;
  phone: string;
  status: USER_STATUS | null;
  roles: string[];
  social_media_instagram: string;
  social_media_facebook: string;
  social_media_tiktok: string;
  'email-application-approved': boolean;
  'email-application-frozen': boolean;
  'subscribe-wishlist-emails': boolean;
  'email-application-cancelled': boolean;
  'marketing-emails-opt-in': boolean;
  generated_referral_code: string;
  authentication_mechanism: 'password';
  stripe_subscription_id: string;
  stripe_customer_id: string;
  brand_like: string;
  pending_cancellation_date: Date;
  cancellation_date?: Date | null;
  reactivation_date?: Date | null; // Date when user reactivated their account
  application_date: Date;
  approval_date: Date;
  subscription_order_id: string;
  account_flag?: string;
  warning_flags?: string;
  subscribed_sms?: boolean;
  auto_approved?: boolean | undefined;
  promo_code?: string; // Custom EPCC flow field for storing promo codes during reactivation
  referrer_code?: string; // Referral / partner promo code the member signed up with
  subscription_level?: string; // EPCC flow field for restoring subscription level during reactivation
  addon_subscription?: string; // EPCC flow field for storing Premier add-on during reactivation
  cancellation_minimum_waiver_months?: number | null; // EPCC flow field (ADR 0007); cleared (null) on reactivation
  cancellation_minimum_waiver_source?: string | null; // Provenance of the waiver, e.g. 'promo:<id>'; cleared on reactivation
  privee_status?: string;
  privee_acceptance_date?: string;
  privee_frozen_date?: string;
  interested_in_privee?: boolean;
  interested_in_privee_date?: string;
  brand_ambassador?: boolean; // Whether the user is a Vivrelle Brand Ambassador
};

export enum USER_STATUS {
  ACTIVE = 'Active',
  PENDING = 'Pending',
  PENDING_MORE_INFO = 'Pending - More Info',
  WAITLIST = 'Waitlist',
  FROZEN = 'Frozen',
  DENIED = 'Denied',
  CANCELLED = 'Cancelled',
  LOCKED = 'Locked',
  PAYMENT_NEEDED = 'Payment Needed',
  PAST_DUE = 'Past Due'
}

export type EPCCAddress = {
  id: string;
  type: 'address';
  isDefault: boolean;
  isBilling: boolean;
  email: string | null;
  city: string;
  company_name?: string;
  country: string;
  county: string;
  /** 2-char state */
  region: string;
  first_name: string;
  last_name: string;
  instructions: string;
  line_1: string;
  line_2?: string;
  name: string;
  phone_number: string;
  postcode: string;
  meta: {
    timestamps: {
      /** ISO8601 Date string */
      created_at: string;
      /** ISO8601 Date string */
      updated_at: string;
    };
  };
};

export type AppointmentType = {
  calendar_event: {
    external_id: string;
    kind: string;
  };
  created_at: string;
  end_time: string;
  event_guests: string[];
  event_memberships: [
    {
      user: string;
      user_email: string;
      user_name: string;
    }
  ];
  event_type: string;
  invitees_counter: {
    active: number;
    limit: number;
    total: number;
  };
  location: {
    additional_info: string;
    location: string;
    type: string;
  };
  meeting_notes_html: null;
  meeting_notes_plain: null;
  name: string;
  start_time: string;
  status: string;
  updated_at: string;
  uri: string;
};
